import { createContext, useContext } from 'react';

type RadioGroupContextValue = {
  ariaDescribedby?: string;
};

export const RadioGroupContext = createContext<RadioGroupContextValue | undefined>(undefined);

export const useRadioGroup = () => {
  const context = useContext(RadioGroupContext);

  if (context === undefined) {
    throw new Error(
      `useRadioCard must be used within a RadioGroupContext.Provider. Make sure to place your Radio component inside a RadioGroup.`,
    );
  }

  return context;
};

/**
 * Note on ariaDescribedby and RadioGroupContext
 *
 * When a group of radio buttons is invalid and requires an error, it should be announced by assistive technology. This
 * can be done by describing the group with the error, using aria-describedby. However, when using VoiceOver the error
 * will not be announced (see https://russmaxdesign.github.io/accessible-forms/fieldset-error02.html). On error, when a
 * form is submitted, a radio input will be focussed when it's the first error in the form. The error won't be announced
 * by VoiceOver.
 *
 * We can circumvent this by adding aria-describedby to every radio input instead of the radio group. The downside will
 * be that all screen readers will read the error for every radio input, but the upside is that we're not excluding
 * VoiceOver. For hints we only need to describe the radio group, and not every single radio input because hints are not
 * blocking the user from continuing.
 */
